import React, {useEffect} from 'react'
import Layout from '../../components/layouts/Layout'
import { useDispatch, useSelector } from 'react-redux'
import { fetchDashboardData } from '../../store/actions/dashboardActions'
import { fetchApplications } from '../../store/actions/applicationsActions'
import { fetchPayments } from '../../store/actions/paymentsActions'
import Loader from '../../components/elements/Loader'
import DataTable from '../../components/elements/DataTable'
import { tableHeadersFields, transactionTimeStamp } from '../../utils'
import ApplicationStatus from '../../components/elements/ApplicationStatus'
import ApplicantTableSnippet from '../../components/partials/applications/ApplicantTableSnippet'
import OrderStatus from '../../components/partials/OrderStatus'
import { Link } from 'react-router-dom'
import ArrowIcon from '../../components/elements/icons/ArrowIcon'
import EmptyState from '../../components/elements/EmptyState'

const Dashboard = () => {
    const dispatch = useDispatch()
    const dashboardSelector = useSelector((state) => state.dashboard)
    const applicationsSelector = useSelector((state) => state.applications)
    const paymentsSelector = useSelector((state) => state.payments)
    const academicSessionsSelector = useSelector((state) => state.academicSessions)
    const termsSelector = useSelector((state) => state.terms)

    useEffect(() => {
        dispatch(fetchDashboardData())
        dispatch(fetchApplications('', 1, 5))
        dispatch(fetchPayments('', 1, 5))
    }, [dispatch])

    // Clean up applications data for table
    const cleanUpApplicationsData = (array) => {
        if(!array) return []
        return array.map((object) => ({
            applicant: <ApplicantTableSnippet applicant={object} />,
            applicationCode: object.applicationCode,
            class: object.class,
            status: <ApplicationStatus status={object.status} />,
            date: transactionTimeStamp(object.createdAt).date,
        }))
    }

    // Clean up payments data for table
    const TransactionLink = ({reference, index}) => (
        <button onClick={()=>console.log('Transaction clicked', index)} className='text-vcm-purple hover:text-gray-600 transition duration-200'>
            {reference}
        </button>
    )

    const TransactionAmount = ({amount}) => (
        <p className='font-medium'>N {amount ? (amount/100)?.toLocaleString() : 0}</p>
    )

    const cleanUpPaymentsData = (array) => {
        if(!array) return []
        return array.map((item, itemIndex) => ({
            reference: <TransactionLink reference={item.transaction?.transactionReference || 'N/A'} index={itemIndex} />,
            paymentFor: <p className='text-sm'>{item.fee ? `${item.fee?.name}` : item.invoiceAssignment?.invoiceName || 'N/A'}</p>,
            status: <OrderStatus status={item.transaction?.status || 'pending'} />,
            paidBy: <div><p className='text-sm'>{item.createdBy?.name || ''}</p></div>,
            amount: <TransactionAmount amount={item?.fee?.amount || item.invoiceAssignment?.amountPaid || 0} />,
            timeStamp: transactionTimeStamp(item.createdAt).date,
        }))
    }

    const applicationsColumnWidths = {
        applicant: 'w-4/12',
        applicationCode: 'w-2/12',
        class: 'w-2/12',
        status: 'w-2/12',
        date: 'w-2/12',
    }

    const paymentsColumnWidths = {
        reference: 'w-2/12',
        paymentFor: 'w-3/12',
        status: 'w-2/12',
        paidBy: 'w-2/12',
        amount: 'w-2/12',
        timeStamp: 'w-2/12',
    }

    const tableOptions = {
        selectable: false,
        clickableRows: false,
    }

    const studentsData = dashboardSelector.dashboardData?.students || {}
    const activeStudents = studentsData.active || 0
    const totalStudents = studentsData.total || 0
    const newStudentsThisMonth = studentsData.newThisMonth || 0
    const maleStudents = studentsData.male || 0
    const femaleStudents = studentsData.female || 0

    const applicationsData = dashboardSelector.dashboardData?.applications || {}
    const totalApplications = applicationsData.total || 0
    const pendingApplications = applicationsData.pending || 0
    const approvedApplications = applicationsData.approved || 0
    const rejectedApplications = applicationsData.rejected || 0
    const newApplicationsThisMonth = applicationsData.newThisMonth || 0

    const pendingInvoicesCount = dashboardSelector.dashboardData?.invoices?.pending || 0
    const pendingInvoicesAmount = dashboardSelector.dashboardData?.invoices?.pendingAmount || 0

    // Get current session and term (you'll need to add the logic to fetch active session/term)
    const currentSession = academicSessionsSelector?.academicSessions?.academicSessions?.find(s => s.current)
    const currentTerm = termsSelector?.terms?.terms?.find(t => t.currentlyActive)

    const currency = (amount) => `₦${((amount || 0) / 100).toLocaleString()}`
    const overviewCards = [
        { label: 'Students', value: totalStudents, detail: `${activeStudents} active`, tone: 'bg-vcm-purple text-white', link: '/admin/students' },
        { label: 'Applications', value: totalApplications, detail: `${pendingApplications} awaiting review`, tone: 'bg-white text-gray-900', link: '/admin/administration/applications' },
        { label: 'Pending invoices', value: pendingInvoicesCount, detail: currency(pendingInvoicesAmount) + ' outstanding', tone: 'bg-white text-gray-900', link: '/admin/accounting/invoices' },
        { label: 'New this month', value: newStudentsThisMonth + newApplicationsThisMonth, detail: `${newStudentsThisMonth} students · ${newApplicationsThisMonth} applications`, tone: 'bg-white text-gray-900', link: '/admin/administration/applications' },
    ]

    return (
        <div>
            <Layout pageTitle="Dashboard">
                <div className='w-full max-w-[1600px] mx-auto'>
                    <section className='mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
                        <div>
                            <p className='text-sm text-vcm-dark-gray'>A clear view of your school's activity and what needs attention.</p>
                        </div>
                        <Link to='/admin/administration/applications' className='inline-flex w-fit items-center gap-2 rounded-lg bg-vcm-purple px-4 py-2.5 text-sm font-medium text-white transition hover:bg-vcm-navy'>
                            Review applications <ArrowIcon className='h-4 w-4' />
                        </Link>
                    </section>

                    <section className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-7'>
                        {overviewCards.map((card) => (
                            <Link key={card.label} to={card.link} className={`${card.tone} group rounded-xl border border-gray-200 p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${card.tone.includes('vcm-purple') ? 'border-vcm-purple' : ''}`}>
                                <p className={`text-sm ${card.tone.includes('vcm-purple') ? 'text-white/75' : 'text-vcm-dark-gray'}`}>{card.label}</p>
                                <div className='mt-4 flex items-end justify-between gap-3'>
                                    <p className={`font-space-grotesk text-3xl font-bold ${card.tone.includes('vcm-purple') ? 'text-white' : 'text-vcm-purple'}`}>{card.value}</p>
                                    <ArrowIcon className={`h-4 w-4 transition group-hover:translate-x-1 ${card.tone.includes('vcm-purple') ? 'text-white' : 'text-vcm-purple'}`} />
                                </div>
                                <p className={`mt-2 text-xs ${card.tone.includes('vcm-purple') ? 'text-white/75' : 'text-vcm-dark-gray'}`}>{card.detail}</p>
                            </Link>
                        ))}
                    </section>

                    <section className='mb-7 grid grid-cols-1 gap-5 xl:grid-cols-5'>
                        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-3'>
                            <div className='mb-5 flex items-center justify-between'>
                                <div>
                                    <p className='text-sm font-medium text-gray-900'>Application pipeline</p>
                                    <p className='mt-1 text-xs text-vcm-dark-gray'>Current application status at a glance</p>
                                </div>
                                <Link to='/admin/administration/applications' className='text-xs font-medium text-vcm-purple hover:text-vcm-navy'>View all</Link>
                            </div>
                            <div className='grid grid-cols-3 divide-x divide-gray-100'>
                                <div className='pr-4'><p className='text-xs text-vcm-dark-gray'>Pending</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-amber-600'>{pendingApplications}</p></div>
                                <div className='px-4'><p className='text-xs text-vcm-dark-gray'>Approved</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-emerald-600'>{approvedApplications}</p></div>
                                <div className='pl-4'><p className='text-xs text-vcm-dark-gray'>Rejected</p><p className='mt-2 font-space-grotesk text-2xl font-bold text-rose-600'>{rejectedApplications}</p></div>
                            </div>
                        </div>
                        <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2'>
                            <p className='text-sm font-medium text-gray-900'>Academic context</p>
                            <div className='mt-4 space-y-3 text-sm'>
                                <div className='flex justify-between gap-4'><span className='text-vcm-dark-gray'>Current session</span><span className='font-medium text-gray-900 text-right'>{currentSession?.name || 'Not set'}</span></div>
                                <div className='flex justify-between gap-4 border-t border-gray-100 pt-3'><span className='text-vcm-dark-gray'>Current term</span><span className='font-medium text-gray-900 text-right'>{currentTerm?.name || 'Not set'}</span></div>
                                <div className='flex justify-between gap-4 border-t border-gray-100 pt-3'><span className='text-vcm-dark-gray'>Student balance</span><span className='font-medium text-gray-900'>{maleStudents} male · {femaleStudents} female</span></div>
                            </div>
                        </div>
                    </section>

                    {/* Recent Applications Section */}
                    <section className='bg-white rounded-xl p-5 shadow-sm border border-gray-200 mb-5 overflow-x-auto'>
                        <div className='flex items-center justify-between mb-4'>
                            <h3 className='text-lg font-medium text-gray-800'>Recent Applications</h3>
                            <Link to="/admin/administration/applications" className='text-sm text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
                                View all applications <ArrowIcon className="w-4 h-4" />
                            </Link>
                        </div>
                        {applicationsSelector.loadingApplications ? (
                            <Loader />
                        ) : applicationsSelector.applications?.applications?.length > 0 ? (
                            <DataTable
                                tableHeaders={tableHeadersFields(cleanUpApplicationsData(applicationsSelector.applications.applications)[0]).headers}
                                tableData={cleanUpApplicationsData(applicationsSelector.applications.applications)}
                                columnWidths={applicationsColumnWidths}
                                columnDataStyles={{}}
                                allFields={tableHeadersFields(cleanUpApplicationsData(applicationsSelector.applications.applications)[0]).fields}
                                onSelectItems={()=>{}}
                                tableOptions={tableOptions}
                                pagination={null}
                            />
                        ) : (
                            <EmptyState emptyStateText="No applications yet" />
                        )}
                    </section>

                    {/* Recent Payments Section */}
                    <section className='bg-white rounded-xl p-5 shadow-sm border border-gray-200 overflow-x-auto'>
                        <div className='flex items-center justify-between mb-4'>
                            <h3 className='text-lg font-medium text-gray-800'>Recent Payments</h3>
                            <Link to="/admin/accounting/payments" className='text-sm text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
                                View all payments <ArrowIcon className="w-4 h-4" />
                            </Link>
                        </div>
                        {paymentsSelector.loadingPayments ? (
                            <Loader />
                        ) : paymentsSelector.payments?.payments?.length > 0 ? (
                            <DataTable
                                tableHeaders={tableHeadersFields(cleanUpPaymentsData(paymentsSelector.payments.payments)[0]).headers}
                                tableData={cleanUpPaymentsData(paymentsSelector.payments.payments)}
                                columnWidths={paymentsColumnWidths}
                                columnDataStyles={{}}
                                allFields={tableHeadersFields(cleanUpPaymentsData(paymentsSelector.payments.payments)[0]).fields}
                                onSelectItems={()=>{}}
                                tableOptions={tableOptions}
                                pagination={null}
                            />
                        ) : (
                            <EmptyState emptyStateText="No payments yet" />
                        )}
                    </section>
                </div>
            </Layout>
        </div>
    )
}

export default Dashboard
